import { Button, Card, Checkbox, Form, Input, Modal, Space, Typography, Upload, message } from "antd";
import { useCallback, useMemo, useState } from "react";
import { downloadBlob, serverFetchBinary, serverFetchMultipart } from "../../core/server";

const DatabaseSettings = () => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [operation, setOperation] = useState("");

  const initialValues = useMemo(
    () => ({
      includeTypicalConstructions: true,
      includeReports: true,
      includeBlanks: true,
      includePaymentTypes: true,
      keepMarkupPercent: false,
      message: ""
    }),
    []
  );

  const commonParams = () => {
    const values = form.getFieldsValue();
    return {
      includeTypicalConstructions: values.includeTypicalConstructions !== false,
      includeReports: values.includeReports !== false,
      includeBlanks: values.includeBlanks !== false,
      includePaymentTypes: values.includePaymentTypes !== false
    };
  };

  const exportSettings = useCallback(async () => {
    setOperation("export");
    try {
      const values = form.getFieldsValue();
      const response = await serverFetchBinary("app/database/settings-exchange/export", {
        method: "GET",
        queryParams: { ...commonParams(), message: values.message || "" }
      });
      await downloadBlob(response, "obmen-settings.ite");
      message.success("Настройки выгружены");
    } catch (e) {
      message.error(e.userMessage || e.message || "Не удалось выгрузить настройки");
    } finally {
      setOperation("");
    }
  }, [form]);

  const importSettings = () => {
    const file = fileList[0]?.originFileObj;
    if (!file) {
      message.error("Выберите файл обмена настройками");
      return;
    }
    Modal.confirm({
      title: "Загрузить настройки?",
      content: "Старые настройки будут заменены данными из файла.",
      okText: "Загрузить",
      okButtonProps: { danger: true },
      cancelText: "Отмена",
      onOk: async () => {
        setOperation("import");
        try {
          const values = form.getFieldsValue();
          const data = new FormData();
          data.append("file", file, file.name || "obmen-settings.ite");
          Object.entries({
            ...commonParams(),
            keepMarkupPercent: values.keepMarkupPercent === true
          }).forEach(([key, value]) => data.append(key, String(value)));
          const result = await serverFetchMultipart("app/database/settings-exchange/import", data);
          message.success(`Настройки загружены. Записей: ${result?.recordCount ?? 0}.`);
        } catch (e) {
          message.error(e.userMessage || e.message || "Не удалось загрузить настройки");
          return Promise.reject();
        } finally {
          setOperation("");
        }
      }
    });
  };

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Typography.Paragraph type="secondary">
        Обмен настройками программы через файл `.ite`.
      </Typography.Paragraph>
      <Card size="small" title="Состав обмена">
        <Form form={form} layout="vertical" initialValues={initialValues}>
          <Form.Item name="includeTypicalConstructions" valuePropName="checked">
            <Checkbox>Типовые конструкции</Checkbox>
          </Form.Item>
          <Form.Item name="includeReports" valuePropName="checked">
            <Checkbox>Отчеты</Checkbox>
          </Form.Item>
          <Form.Item name="includeBlanks" valuePropName="checked">
            <Checkbox>Компании</Checkbox>
          </Form.Item>
          <Form.Item name="includePaymentTypes" valuePropName="checked">
            <Checkbox>Виды оплат</Checkbox>
          </Form.Item>
          <Form.Item name="keepMarkupPercent" valuePropName="checked">
            <Checkbox>Оставить процент наценки при загрузке</Checkbox>
          </Form.Item>
          <Form.Item name="message" label="Сообщение">
            <Input.TextArea rows={3} maxLength={1000} />
          </Form.Item>
        </Form>
      </Card>
      <Space>
        <Button type="primary" loading={operation === "export"} disabled={!!operation} onClick={exportSettings}>
          Выгрузить в файл
        </Button>
        <Upload
          accept=".ite,.zip"
          maxCount={1}
          fileList={fileList}
          beforeUpload={() => false}
          onChange={({ fileList: next }) => setFileList(next.slice(-1))}
        >
          <Button disabled={!!operation}>Выбрать файл</Button>
        </Upload>
        <Button danger loading={operation === "import"} disabled={!!operation || !fileList.length} onClick={importSettings}>
          Загрузить из файла
        </Button>
      </Space>
    </Space>
  );
};

export default DatabaseSettings;
